import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { Layout } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import UpcomingEvents from "@/components/UpcomingEvents";
import { Users, AlertTriangle, Calendar, ShieldAlert, ChevronRight, Phone, Plus, Award } from "lucide-react";

export default function KujdestariDashboard() {
    const [params] = useSearchParams();
    const [data, setData] = useState(null);
    const [classes, setClasses] = useState([]);
    const [classId, setClassId] = useState(params.get("class_id") || "");
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        (async () => {
            try {
                const r = await api.get("/kujdestari/my-classes");
                setClasses(r.data);
                if (!classId && r.data.length > 0) setClassId(r.data[0].id);
            } catch { /* ignore */ }
        })();
    }, []);

    useEffect(() => {
        if (!classId) return;
        (async () => {
            setLoading(true);
            try {
                const r = await api.get("/kujdestari/dashboard", { params: { class_id: classId } });
                setData(r.data);
            } finally { setLoading(false); }
        })();
    }, [classId]);

    if (classes.length === 0 && !loading) {
        return (
            <Layout>
                <div className="bg-white border border-slate-200 rounded-2xl p-10 text-center max-w-2xl">
                    <ShieldAlert className="w-12 h-12 text-slate-400 mx-auto mb-4" />
                    <h1 className="font-heading text-2xl font-bold text-slate-800">Nuk jeni caktuar si Kujdestar</h1>
                    <p className="text-sm text-slate-600 mt-2">Administratori duhet t'ju caktojë si kujdestar të një klase nga paneli i Klasave.</p>
                </div>
            </Layout>
        );
    }

    return (
        <Layout>
            <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-3 mb-6">
                <div>
                    <div className="text-xs uppercase tracking-[0.18em] text-slate-500 mb-2">Kujdestari i Klasës</div>
                    <h1 className="font-heading text-3xl sm:text-4xl md:text-5xl font-black text-slate-900 tracking-tight">
                        {data?.class?.name || "Paneli im"}
                    </h1>
                    <p className="text-slate-600 mt-2 text-sm">Statistikat e klasës, mbledhjet me prindër, masat disiplinore</p>
                </div>
                {classes.length > 1 && (
                    <select value={classId} onChange={(e) => setClassId(e.target.value)} className="h-10 rounded-md border border-slate-300 bg-white text-sm px-3 font-semibold" data-testid="kujdestari-class-select">
                        {classes.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                    </select>
                )}
            </div>

            {loading || !data ? <div className="text-slate-500">Duke ngarkuar…</div> : (
                <>
                    {/* KPI cards */}
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
                        <div className="bg-white border border-slate-200 rounded-xl p-4">
                            <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">Numri i nxënësve</div>
                            <div className="font-heading text-3xl font-black text-oxford mt-1" data-testid="kpi-students">{data.totals.students}</div>
                        </div>
                        <div className="bg-white border border-slate-200 rounded-xl p-4">
                            <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">Mungesa javore</div>
                            <div className="font-heading text-3xl font-black text-amber-600 mt-1" data-testid="kpi-weekly-abs">{data.totals.weekly_absences}</div>
                        </div>
                        <div className="bg-white border border-slate-200 rounded-xl p-4">
                            <div className="text-[10px] uppercase tracking-widest text-slate-500 font-bold">Mungesa total</div>
                            <div className="font-heading text-3xl font-black text-slate-700 mt-1" data-testid="kpi-total-abs">{data.totals.total_absences}</div>
                        </div>
                        <div className="bg-terracotta/5 border border-terracotta/30 rounded-xl p-4">
                            <div className="text-[10px] uppercase tracking-widest text-terracotta font-bold">Në rrezik</div>
                            <div className="font-heading text-3xl font-black text-terracotta mt-1" data-testid="kpi-at-risk">{data.totals.at_risk}</div>
                        </div>
                    </div>

                    {/* Quick actions */}
                    <div className="flex flex-wrap gap-2 mb-6">
                        <Link to={`/kujdestari/meetings?class_id=${classId}`} className="inline-flex items-center gap-2 h-11 px-4 rounded-md bg-oxford hover:bg-oxfordhover text-white text-sm font-semibold" data-testid="link-meetings">
                            <Calendar className="w-4 h-4" /> Mbledhjet me prindër
                        </Link>
                        <Link to={`/kujdestari/discipline?class_id=${classId}`} className="inline-flex items-center gap-2 h-11 px-4 rounded-md border border-slate-200 bg-white hover:border-terracotta text-sm font-semibold" data-testid="link-discipline">
                            <ShieldAlert className="w-4 h-4" /> Vërejtje & Pezullime
                        </Link>
                        <Link to={`/kujdestari/students?class_id=${classId}`} className="inline-flex items-center gap-2 h-11 px-4 rounded-md border border-slate-200 bg-white hover:border-oxford text-sm font-semibold" data-testid="link-students">
                            <Users className="w-4 h-4" /> Nxënësit e klasës
                        </Link>
                        <Link to={`/kujdestari/end-of-year-report?class_id=${classId}`} className="inline-flex items-center gap-2 h-11 px-4 rounded-md border border-slate-200 bg-white hover:border-sage text-sm font-semibold" data-testid="link-end-of-year">
                            <Award className="w-4 h-4" /> Raporti i Fundvitit
                        </Link>
                    </div>

                    <div className="mb-5"><UpcomingEvents days={14} limit={4} /></div>

                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
                        {/* At-risk students */}
                        <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden">
                            <div className="px-4 sm:px-5 py-3 border-b border-slate-200 bg-bone/50 flex items-center gap-2">
                                <AlertTriangle className="w-4 h-4 text-terracotta" />
                                <div className="text-xs uppercase tracking-widest font-bold text-slate-700">Nxënës në rrezik</div>
                                <div className="ml-auto text-xs text-slate-500">{data.at_risk.length}</div>
                            </div>
                            {data.at_risk.length === 0 ? (
                                <div className="p-8 text-center text-sm text-slate-500">Asnjë nxënës në rrezik 🎉</div>
                            ) : (
                                <div className="divide-y divide-slate-200 max-h-96 overflow-y-auto">
                                    {data.at_risk.map((s) => (
                                        <div key={s.id} className="px-4 sm:px-5 py-3 flex items-center gap-3" data-testid={`at-risk-${s.id}`}>
                                            <div className="w-9 h-9 rounded-xl bg-terracotta/10 text-terracotta flex items-center justify-center font-bold text-xs shrink-0">
                                                {s.name.split(" ").map((n) => n[0]).slice(0, 2).join("")}
                                            </div>
                                            <div className="flex-1 min-w-0">
                                                <div className="font-bold text-slate-900 truncate text-sm">{s.name}</div>
                                                <div className="text-[11px] text-slate-500">
                                                    {s.weekly_absences > 0 && <span>{s.weekly_absences} mungesa javë</span>}
                                                    {s.warnings > 0 && <span className="ml-2 text-amber-700">⚠ {s.warnings} vërejtje</span>}
                                                    {s.suspensions > 0 && <span className="ml-2 text-terracotta">🟥 {s.suspensions} pezullime</span>}
                                                </div>
                                            </div>
                                            {s.parent_phone && (
                                                <a href={`tel:${s.parent_phone.replace(/\s/g, "")}`} className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-sage/10 text-sage hover:bg-sage/20 text-xs font-semibold border border-sage/30" title="Thirr prindin">
                                                    <Phone className="w-3 h-3" />
                                                </a>
                                            )}
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>

                        {/* Recent discipline */}
                        <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden">
                            <div className="px-4 sm:px-5 py-3 border-b border-slate-200 bg-bone/50 flex items-center gap-2">
                                <ShieldAlert className="w-4 h-4 text-amber-600" />
                                <div className="text-xs uppercase tracking-widest font-bold text-slate-700">Masat e fundit</div>
                                <div className="ml-auto text-xs text-slate-500">{data.discipline_recent.length}</div>
                            </div>
                            {data.discipline_recent.length === 0 ? (
                                <div className="p-8 text-center text-sm text-slate-500">Asnjë masë disiplinore</div>
                            ) : (
                                <div className="divide-y divide-slate-200 max-h-96 overflow-y-auto">
                                    {data.discipline_recent.map((d) => (
                                        <div key={d.id} className="px-4 sm:px-5 py-3" data-testid={`disc-${d.id}`}>
                                            <div className="flex items-center gap-2 mb-1">
                                                <span className={`inline-flex items-center px-2 py-0.5 rounded-full border text-[10px] font-bold ${d.severity === "warning" ? "bg-amber-50 text-amber-700 border-amber-200" : "bg-terracotta/10 text-terracotta border-terracotta/30"}`}>
                                                    {d.severity === "warning" ? "Vërejtje" : "Pezullim"}
                                                </span>
                                                <span className="font-bold text-slate-900 text-sm">{d.student_name}</span>
                                                <span className="ml-auto text-[10px] text-slate-500 font-mono">{d.created_at?.slice(0, 10)}</span>
                                            </div>
                                            <p className="text-xs text-slate-600 leading-relaxed">{d.reason}</p>
                                            <div className="text-[10px] text-slate-400 mt-1">— {d.created_by_name}</div>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Recent meetings */}
                    {data.meetings.length > 0 && (
                        <div className="bg-white border border-slate-200 rounded-2xl overflow-hidden mt-5">
                            <div className="px-4 sm:px-5 py-3 border-b border-slate-200 bg-bone/50 flex items-center gap-2">
                                <Calendar className="w-4 h-4 text-oxford" />
                                <div className="text-xs uppercase tracking-widest font-bold text-slate-700">Mbledhjet e fundit me prindër</div>
                            </div>
                            <div className="divide-y divide-slate-200">
                                {data.meetings.map((m) => (
                                    <Link key={m.id} to={`/kujdestari/meetings?class_id=${classId}#${m.id}`} className="px-4 sm:px-5 py-3 flex items-center gap-3 hover:bg-bone/30">
                                        <div className="text-xs font-mono text-slate-500 w-24">{m.date} {m.time}</div>
                                        <div className="flex-1 truncate text-sm">
                                            {(m.agenda || []).length} pika në rendin e ditës
                                            {m.conclusion && <span className="text-slate-500"> • {m.conclusion.slice(0, 40)}…</span>}
                                        </div>
                                        <ChevronRight className="w-4 h-4 text-slate-400" />
                                    </Link>
                                ))}
                            </div>
                        </div>
                    )}
                </>
            )}
        </Layout>
    );
}
